'use client';
import React from 'react';
import OrderButton from './OrderButton';

const features = [
  {
    id: 'feat-instant',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Instant Telegram Delivery',
    description: 'Access is pushed to your Telegram the moment your BTC transaction clears the required confirmation threshold. No waiting, no manual review.',
  },
  {
    id: 'feat-chain',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    title: 'Blockchain-Verified Payment',
    description: 'Every transaction is validated in real-time against the Bitcoin network — recipient address, amount, and confirmation count all verified before access is granted.',
  },
  {
    id: 'feat-private',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    title: 'Zero Data Stored',
    description: 'No account creation, no email, no personal data. Your transaction hash is verified in real-time and discarded. Nothing is persisted beyond your session.',
  },
  {
    id: 'feat-btc',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Bitcoin Payments Only',
    description: 'Pay via the Bitcoin mainnet. Send the exact BTC equivalent of $100 (rate shown at checkout) to the provided wallet address. No other networks accepted.',
  },
  {
    id: 'feat-hash',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    title: 'Submit Your TX Hash',
    description: 'After sending payment, paste your transaction hash from any BTC explorer. The system validates it against the blockchain in seconds — no manual approval needed.',
  },
  {
    id: 'feat-content',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    title: 'Telegram-Exclusive Content',
    description: 'All content lives inside a private Telegram channel. Nothing is hosted on this website. Your access link is delivered via bot message immediately after verification.',
  },
];

export default function OfferSection() {
  return (
    <section id="offer" className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="divider-gold mb-16 sm:mb-24" />
      <div className="max-w-screen-xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-16 flex flex-col items-center gap-3 sm:gap-4">
          <span className="badge-limited">What you get</span>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-foreground">
            Everything in one{' '}
            <span className="text-gradient-gold">instant delivery</span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl">
            Luminary X Pro is a focused digital resource. Pay once in BTC, receive access once via Telegram. No subscriptions, no recurring charges, no accounts.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-16">
          {features?.map((feat) => (
            <div key={feat?.id} className="offer-feature-card p-4 sm:p-6 flex flex-col gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-primary flex-shrink-0"
                style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                {feat?.icon}
              </div>
              <div className="flex flex-col gap-1.5 sm:gap-2">
                <h3 className="text-sm sm:text-base font-bold text-foreground">{feat?.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{feat?.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* How it works strip */}
        <div className="card-elevated p-5 sm:p-8 sm:p-10 mb-10 sm:mb-16">
          <h3 className="text-lg sm:text-xl font-bold text-foreground text-center mb-6 sm:mb-10">How it works</h3>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { step: '01', title: 'Click Order Access', desc: 'Opens the checkout panel with your wallet address and exact BTC amount.' },
              { step: '02', title: 'Send BTC', desc: 'Transfer the exact amount from your Bitcoin wallet to the displayed address.' },
              { step: '03', title: 'Paste TX Hash', desc: 'Copy your transaction hash from any BTC explorer and paste it into the field.' },
              { step: '04', title: 'Get Telegram Access', desc: 'Blockchain confirms your payment. Bot delivers your access link instantly.' },
            ]?.map((item, i) => (
              <div key={`step-${item?.step}`} className="flex flex-col gap-2 sm:gap-3 relative">
                {i < 3 && (
                  <div className="hidden lg:block absolute top-5 left-full w-full h-px z-10"
                    style={{ background: 'linear-gradient(90deg, rgba(245,158,11,0.3), transparent)', width: 'calc(100% - 3rem)', marginLeft: '1.5rem' }} />
                )}
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center font-mono-data text-xs sm:text-sm font-bold text-primary"
                  style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                  {item?.step}
                </div>
                <h4 className="font-bold text-foreground text-xs sm:text-sm">{item?.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA strip */}
        <div className="text-center flex flex-col items-center gap-4 sm:gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <OrderButton size="lg" />
            <div className="text-center sm:text-left">
              <div className="text-xl sm:text-2xl font-extrabold text-foreground">$100 <span className="text-muted-foreground text-base font-medium">one-time</span></div>
              <div className="text-xs sm:text-sm text-muted-foreground">≈ 0.00098 BTC · Rate locked at checkout</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground max-w-sm">
            Payment is non-refundable once blockchain confirmation is received and access is delivered.
          </p>
        </div>
      </div>
      <div className="divider-gold mt-16 sm:mt-24" />
    </section>
  );
}